import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db, functions } from '../../firebase';
import {
  CheckCircleIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';

const PREQUALIFIED_STATUSES = ['prequalified', 'prequalified_for', 'complete', 'approved'];
const UNDERWRITING_STATUSES = ['underwriting', 'in underwriting', 'submitted to lender', 'lender review', 'term sheet signed', 'funded'];

const money = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const statusOf = (record) => String(
  record.loanStatus || record.loanapplicationstatus || record.status || record.applicationStatus || ''
).trim().toLowerCase();

const brokerName = (broker) => (
  [broker.firstName, broker.lastName].filter(Boolean).join(' ')
  || broker.name
  || broker.brokerageName
  || broker.brokerAgeName
  || broker.email
  || 'Unknown broker'
);

const dealName = (deal) => (
  deal.businessName
  || deal.businesses?.find((business) => business.businessName)?.businessName
  || [deal.firstName, deal.lastName].filter(Boolean).join(' ')
  || 'Unnamed deal'
);

const isPrequalified = (listing) => (
  PREQUALIFIED_STATUSES.includes(statusOf(listing))
  || listing.prequalified === true
  || Number(listing.prequalifiedAmount) > 0
);

const isUnderwriting = (record) => (
  UNDERWRITING_STATUSES.some((status) => statusOf(record).includes(status))
  || record.inUnderwriting === true
  || record.underwritingStarted === true
  || Boolean(record.underwritingStartedAt)
);

const timestampMs = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  return new Date(value).getTime() || 0;
};

export default function SalesCommissionHub({ canManageReps = false, repOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [reps, setReps] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [claims, setClaims] = useState({});
  const [listings, setListings] = useState([]);
  const [deals, setDeals] = useState([]);
  const [commissionDeals, setCommissionDeals] = useState({});
  const [milestoneState, setMilestoneState] = useState({});
  const [search, setSearch] = useState('');
  const [repFilter, setRepFilter] = useState('all');
  const [newRep, setNewRep] = useState({ name: '', email: '', phone: '' });
  const [showAddRep, setShowAddRep] = useState(false);

  const currentUser = getAuth().currentUser;
  const currentEmail = currentUser?.email?.toLowerCase() || '';
  const currentRep = reps.find((rep) => rep.email?.toLowerCase() === currentEmail);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [repSnap, brokerSnap, claimSnap, listingSnap, dealSnap, commissionSnap, milestoneSnap] = await Promise.all([
        getDocs(collection(db, 'salesReps')),
        getDocs(collection(db, 'brokers')),
        getDocs(collection(db, 'brokerClaims')),
        getDocs(collection(db, 'prequalificationApplications')),
        getDocs(collection(db, 'loanApplications')),
        getDocs(collection(db, 'commissionDeals')),
        getDocs(collection(db, 'salesCommissionState')),
      ]);

      setReps(repSnap.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setBrokers(brokerSnap.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => brokerName(a).localeCompare(brokerName(b))));
      setClaims(Object.fromEntries(claimSnap.docs.map((item) => [item.id, { id: item.id, ...item.data() }])));
      setListings(listingSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
      setDeals(dealSnap.docs.map((item) => ({ id: item.id, ...item.data() })));
      setCommissionDeals(Object.fromEntries(commissionSnap.docs.map((item) => [item.id, { id: item.id, ...item.data() }])));
      setMilestoneState(Object.fromEntries(milestoneSnap.docs.map((item) => [item.id, { id: item.id, ...item.data() }])));
    } catch (error) {
      console.error('Could not load sales commissions:', error);
      alert(`Could not load commissions: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const brokerMetrics = useMemo(() => Object.fromEntries(brokers.map((broker) => {
    const brokerListings = listings.filter((listing) => listing.brokerId === broker.id);
    const listingIds = new Set(brokerListings.map((listing) => listing.id));
    const brokerDeals = deals.filter((deal) => deal.brokerId === broker.id || listingIds.has(deal.prequalificationId));
    const firstPrequalified = brokerListings
      .filter(isPrequalified)
      .sort((a, b) => timestampMs(a.prequalifiedAt || a.lastUpdated) - timestampMs(b.prequalifiedAt || b.lastUpdated))[0];
    const firstUnderwriting = [...brokerDeals, ...brokerListings]
      .filter(isUnderwriting)
      .sort((a, b) => timestampMs(a.underwritingStartedAt || a.lastUpdated) - timestampMs(b.underwritingStartedAt || b.lastUpdated))[0];
    return [broker.id, {
      listings: brokerListings,
      deals: brokerDeals,
      firstPrequalified,
      firstUnderwriting,
    }];
  })), [brokers, deals, listings]);

  const rows = useMemo(() => brokers.map((broker) => {
    const claim = claims[broker.id];
    const rep = reps.find((item) => item.id === claim?.repId);
    const metrics = brokerMetrics[broker.id] || { listings: [], deals: [] };
    const state = milestoneState[broker.id] || {};
    const revenueRows = metrics.deals.map((deal) => {
      const saved = commissionDeals[deal.id] || {};
      const revenue = Number(saved.revenue ?? deal.lendedRevenue ?? deal.revenueCollected ?? deal.feeRevenue ?? 0);
      const serviceLevel = saved.serviceLevel || 'handoff';
      const rate = serviceLevel === 'full_service' ? 0.25 : 0.10;
      return {
        deal,
        saved,
        brokerId: broker.id,
        repId: claim?.repId || '',
        revenue,
        serviceLevel,
        rate,
        commission: revenue * rate,
      };
    });
    const prequalCommission = claim && metrics.firstPrequalified ? 500 : 0;
    const underwritingCommission = claim && metrics.firstUnderwriting ? 500 : 0;
    const revenueCommission = claim ? revenueRows.reduce((sum, row) => sum + row.commission, 0) : 0;
    const paid = (state.prequalPaid ? prequalCommission : 0)
      + (state.underwritingPaid ? underwritingCommission : 0)
      + revenueRows.reduce((sum, row) => sum + (row.saved.paid ? row.commission : 0), 0);
    return {
      broker,
      claim,
      rep,
      metrics,
      state,
      revenueRows,
      prequalCommission,
      underwritingCommission,
      revenueCommission,
      total: prequalCommission + underwritingCommission + revenueCommission,
      paid,
    };
  }), [brokerMetrics, brokers, claims, commissionDeals, milestoneState, reps]);

  const repSummaries = useMemo(() => reps.map((rep) => {
    const repRows = rows.filter((row) => row.claim?.repId === rep.id);
    const earned = repRows.reduce((sum, row) => sum + row.total, 0);
    const paid = repRows.reduce((sum, row) => sum + row.paid, 0);
    return { rep, brokerCount: repRows.length, earned, paid, due: earned - paid };
  }), [reps, rows]);

  const totals = useMemo(() => repSummaries.reduce((result, item) => ({
    earned: result.earned + item.earned,
    paid: result.paid + item.paid,
    due: result.due + item.due,
  }), { earned: 0, paid: 0, due: 0 }), [repSummaries]);

  const visibleRows = rows.filter((row) => {
    const text = `${brokerName(row.broker)} ${row.broker.email || ''} ${row.broker.brokerageName || ''}`.toLowerCase();
    const matchesSearch = text.includes(search.toLowerCase());
    const matchesRep = repFilter === 'all'
      || (repFilter === 'unclaimed' && !row.claim)
      || row.claim?.repId === repFilter;
    return matchesSearch && matchesRep && (!repOnly || row.claim?.repId === currentUser?.uid);
  });

  const addRep = async (event) => {
    event.preventDefault();
    if (!newRep.name.trim() || !newRep.email.trim()) return;
    setSaving('new-rep');
    try {
      const createSalesRepAccount = httpsCallable(functions, 'createSalesRepAccount');
      await createSalesRepAccount({ name: newRep.name.trim(), email: newRep.email.trim(), phone: newRep.phone.trim() });
      setNewRep({ name: '', email: '', phone: '' });
      setShowAddRep(false);
      alert('Rep account created. A password setup email has been sent.');
      await loadData();
    } finally {
      setSaving('');
    }
  };

  const claimBroker = async (broker, repId) => {
    const rep = reps.find((item) => item.id === repId);
    if (!rep) return;
    const existing = claims[broker.id];
    if (existing && existing.repId !== repId && !window.confirm(`${brokerName(broker)} is already claimed by ${existing.repName || 'another rep'}. Reassign to ${rep.name}?`)) return;
    setSaving(`claim-${broker.id}`);
    try {
      await setDoc(doc(db, 'brokerClaims', broker.id), {
        brokerId: broker.id,
        brokerName: brokerName(broker),
        brokerEmail: broker.email || '',
        repId: rep.id,
        repName: rep.name,
        repEmail: rep.email,
        claimedAt: existing?.claimedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        claimedBy: currentEmail,
      }, { merge: true });
      await loadData();
    } finally {
      setSaving('');
    }
  };

  const unclaimBroker = async (brokerId) => {
    if (!window.confirm('Remove this broker claim? Earned commission history will remain visible after the broker is claimed again.')) return;
    setSaving(`claim-${brokerId}`);
    try {
      await deleteDoc(doc(db, 'brokerClaims', brokerId));
      await loadData();
    } finally {
      setSaving('');
    }
  };

  const saveDealCommission = async (row, revenue, serviceLevel) => {
    setSaving(`deal-${row.deal.id}`);
    try {
      await setDoc(doc(db, 'commissionDeals', row.deal.id), {
        loanApplicationId: row.deal.id,
        brokerId: row.brokerId,
        repId: row.repId,
        dealName: dealName(row.deal),
        revenue: Number(revenue) || 0,
        serviceLevel,
        commissionRate: serviceLevel === 'full_service' ? 0.25 : 0.10,
        updatedAt: serverTimestamp(),
        updatedBy: currentEmail,
        paid: row.saved.paid === true,
      }, { merge: true });
      await loadData();
    } finally {
      setSaving('');
    }
  };

  const setPaid = async (collectionName, id, field, value) => {
    setSaving(`${collectionName}-${id}-${field}`);
    try {
      await setDoc(doc(db, collectionName, id), {
        [field]: value,
        [`${field}At`]: value ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: currentEmail,
      }, { merge: true });
      await loadData();
    } finally {
      setSaving('');
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center text-sm text-gray-500">Loading broker commissions…</div>;
  }

  return (
    <div className="space-y-8">
      <section className="overflow-hidden rounded-2xl bg-slate-950 text-white shadow-sm">
        <div className="grid gap-8 px-6 py-8 lg:grid-cols-[1.4fr_1fr] lg:px-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">Broker sales command center</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight">Claims, milestones, and commissions</h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-300">A rep owns the broker relationship after claiming it. Lended automatically recognizes the first prequalified listing and first underwriting milestone; revenue commission is based on whether the rep fully services the deal or hands it off.</p>
          </div>
          <div className="grid grid-cols-3 gap-3 self-end">
            <Metric label="Earned" value={money(totals.earned)} color="text-blue-300" />
            <Metric label="Paid" value={money(totals.paid)} color="text-emerald-300" />
            <Metric label="Due" value={money(totals.due)} color="text-amber-300" />
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <RuleCard amount="$500" title="First listing prequalified" description="Paid once per claimed broker when their first listing reaches prequalified status." />
        <RuleCard amount="$500" title="First underwriting milestone" description="Paid once per claimed broker when any linked listing or deal reaches underwriting." />
        <RuleCard amount="25%" title="Full-service revenue" description="Rep collects all documents and owns all borrower/broker communications." />
        <RuleCard amount="10%" title="Handoff revenue" description="Rep sources and qualifies the relationship, then passes execution to the team." />
      </section>

      {canManageReps && (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><h3 className="text-lg font-bold text-gray-900">Sales reps</h3><p className="text-sm text-gray-500">Add reps here, then they can claim brokers in Ben Portal.</p></div>
            <button type="button" onClick={() => setShowAddRep((value) => !value)} className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"><PlusIcon className="h-4 w-4" /> Add rep</button>
          </div>
          {showAddRep && (
            <form onSubmit={addRep} className="mt-5 grid gap-3 rounded-xl bg-gray-50 p-4 md:grid-cols-[1fr_1fr_1fr_auto]">
              <input required value={newRep.name} onChange={(e) => setNewRep({ ...newRep, name: e.target.value })} placeholder="Rep name" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <input required type="email" value={newRep.email} onChange={(e) => setNewRep({ ...newRep, email: e.target.value })} placeholder="Email" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <input value={newRep.phone} onChange={(e) => setNewRep({ ...newRep, phone: e.target.value })} placeholder="Phone" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <button disabled={saving === 'new-rep'} className="rounded-lg bg-slate-950 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving === 'new-rep' ? 'Adding…' : 'Add rep'}</button>
            </form>
          )}
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {repSummaries.map(({ rep, brokerCount, earned, paid, due }) => (
              <div key={rep.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3"><div><p className="font-semibold text-gray-900">{rep.name}</p><p className="text-xs text-gray-500">{rep.email}</p></div><span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">{brokerCount} brokers</span></div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-xs"><SmallMetric label="Earned" value={money(earned)} /><SmallMetric label="Paid" value={money(paid)} /><SmallMetric label="Due" value={money(due)} emphasis /></div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-200 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div><h3 className="text-lg font-bold text-gray-900">Broker ownership</h3><p className="text-sm text-gray-500">Claim or assign brokers and see commission progress at a glance.</p></div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative"><MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search brokers…" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm sm:w-64" /></div>
            <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="all">All reps</option><option value="unclaimed">Unclaimed</option>{reps.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {visibleRows.map((row) => (
            <BrokerCommissionRow
              key={row.broker.id}
              row={row}
              reps={reps}
              currentRep={currentRep}
              saving={saving}
              canManageReps={canManageReps}
              onClaim={claimBroker}
              onUnclaim={unclaimBroker}
              onSaveDeal={saveDealCommission}
              onSetPaid={setPaid}
            />
          ))}
          {visibleRows.length === 0 && <div className="p-10 text-center text-sm text-gray-500">No brokers match this view.</div>}
        </div>
      </section>
    </div>
  );
}

function BrokerCommissionRow({ row, reps, currentRep, saving, canManageReps, onClaim, onUnclaim, onSaveDeal, onSetPaid }) {
  const [open, setOpen] = useState(false);
  const [selectedRep, setSelectedRep] = useState(row.claim?.repId || currentRep?.id || '');
  return (
    <div className="p-5">
      <div className="grid gap-4 lg:grid-cols-[1.4fr_.8fr_.8fr_.8fr_auto] lg:items-center">
        <button type="button" onClick={() => setOpen((value) => !value)} className="text-left">
          <div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 font-bold text-blue-700">{brokerName(row.broker).slice(0, 2).toUpperCase()}</div><div><p className="font-semibold text-gray-900">{brokerName(row.broker)}</p><p className="text-xs text-gray-500">{row.broker.brokerageName || row.broker.email || 'No brokerage'}</p></div></div>
        </button>
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-400">Owner</p><p className="mt-1 text-sm font-semibold text-gray-800">{row.rep?.name || 'Unclaimed'}</p></div>
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-400">Pipeline</p><p className="mt-1 text-sm font-semibold text-gray-800">{row.metrics.listings.length} listings · {row.metrics.deals.length} deals</p></div>
        <div><p className="text-xs font-medium uppercase tracking-wide text-gray-400">Commission</p><p className="mt-1 text-sm font-bold text-indigo-700">{money(row.total)} <span className="font-normal text-gray-400">· {money(row.total - row.paid)} due</span></p></div>
        <div className="flex items-center gap-2">
          {!row.claim ? <><select value={selectedRep} onChange={(e) => setSelectedRep(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-2 text-xs"><option value="">Select rep</option>{reps.map((rep) => <option key={rep.id} value={rep.id}>{rep.name}</option>)}</select><button type="button" disabled={!selectedRep || saving === `claim-${row.broker.id}`} onClick={() => onClaim(row.broker, selectedRep)} className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40">Claim</button></> : <><button type="button" onClick={() => setOpen((value) => !value)} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700">{open ? 'Close' : 'Details'}</button>{canManageReps && <button type="button" title="Remove claim" onClick={() => onUnclaim(row.broker.id)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><XMarkIcon className="h-4 w-4" /></button>}</>}
        </div>
      </div>
      {open && row.claim && (
        <div className="mt-5 grid gap-5 rounded-xl bg-gray-50 p-5 xl:grid-cols-[.8fr_1.5fr]">
          <div className="space-y-3">
            <Milestone title="First listing prequalified" earned={Boolean(row.metrics.firstPrequalified)} amount={row.prequalCommission} paid={row.state.prequalPaid} disabled={!canManageReps} busy={saving.includes(row.broker.id)} onPaid={(value) => onSetPaid('salesCommissionState', row.broker.id, 'prequalPaid', value)} />
            <Milestone title="First listing/deal in underwriting" earned={Boolean(row.metrics.firstUnderwriting)} amount={row.underwritingCommission} paid={row.state.underwritingPaid} disabled={!canManageReps} busy={saving.includes(row.broker.id)} onPaid={(value) => onSetPaid('salesCommissionState', row.broker.id, 'underwritingPaid', value)} />
          </div>
          <div>
            <div className="mb-3 flex items-center justify-between"><h4 className="text-sm font-bold text-gray-900">Revenue commissions</h4><span className="text-xs text-gray-500">25% full service · 10% handoff</span></div>
            <div className="space-y-2">
              {row.revenueRows.map((dealRow) => <DealCommission key={dealRow.deal.id} row={dealRow} saving={saving} canEdit={canManageReps || row.rep?.id === currentRep?.id} onSave={onSaveDeal} onPaid={(value) => onSetPaid('commissionDeals', dealRow.deal.id, 'paid', value)} />)}
              {row.revenueRows.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-xs text-gray-500">No full deals linked to this broker yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DealCommission({ row, saving, canEdit, onSave, onPaid }) {
  const [revenue, setRevenue] = useState(row.revenue || '');
  const [serviceLevel, setServiceLevel] = useState(row.serviceLevel);
  return <div className="grid gap-2 rounded-lg border border-gray-200 bg-white p-3 md:grid-cols-[1fr_130px_150px_110px_auto] md:items-center"><div><p className="text-sm font-semibold text-gray-900">{dealName(row.deal)}</p><p className="text-xs text-gray-500">{statusOf(row.deal) || 'No status'}</p></div><input type="number" min="0" value={revenue} disabled={!canEdit} onChange={(e) => setRevenue(e.target.value)} placeholder="Revenue" className="rounded-lg border border-gray-300 px-2.5 py-2 text-sm disabled:bg-gray-50" /><select value={serviceLevel} disabled={!canEdit} onChange={(e) => setServiceLevel(e.target.value)} className="rounded-lg border border-gray-300 px-2.5 py-2 text-sm disabled:bg-gray-50"><option value="handoff">Handoff · 10%</option><option value="full_service">Full service · 25%</option></select><p className="text-sm font-bold text-indigo-700">{money((Number(revenue) || 0) * (serviceLevel === 'full_service' ? 0.25 : 0.10))}</p><div className="flex gap-1">{canEdit && <button type="button" disabled={saving === `deal-${row.deal.id}`} onClick={() => onSave(row, revenue, serviceLevel)} className="rounded-lg bg-slate-900 px-2.5 py-2 text-xs font-semibold text-white disabled:opacity-50">Save</button>}<button type="button" disabled={!canEdit || !Number(revenue)} onClick={() => onPaid(!row.saved.paid)} className={`rounded-lg px-2.5 py-2 text-xs font-semibold ${row.saved.paid ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'} disabled:opacity-40`}>{row.saved.paid ? 'Paid' : 'Unpaid'}</button></div></div>;
}

function Milestone({ title, earned, amount, paid, disabled, busy, onPaid }) {
  return <div className={`rounded-xl border p-4 ${earned ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-white'}`}><div className="flex items-start justify-between gap-3"><div className="flex gap-2"><CheckCircleIcon className={`mt-0.5 h-5 w-5 ${earned ? 'text-emerald-600' : 'text-gray-300'}`} /><div><p className="text-sm font-semibold text-gray-900">{title}</p><p className="mt-1 text-xs text-gray-500">{earned ? `Earned · ${money(amount)}` : 'Not reached yet'}</p></div></div>{earned && <button type="button" disabled={disabled || busy} onClick={() => onPaid(!paid)} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${paid ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600'} disabled:opacity-50`}>{paid ? 'Paid' : 'Mark paid'}</button>}</div></div>;
}

function Metric({ label, value, color }) {
  return <div className="rounded-xl bg-white/10 p-3"><p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p><p className={`mt-1 text-lg font-bold ${color}`}>{value}</p></div>;
}

function SmallMetric({ label, value, emphasis }) {
  return <div><p className="text-gray-400">{label}</p><p className={`mt-0.5 font-bold ${emphasis ? 'text-amber-600' : 'text-gray-800'}`}>{value}</p></div>;
}

function RuleCard({ amount, title, description }) {
  return <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-white to-blue-50 p-5 shadow-sm"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white"><CurrencyDollarIcon className="h-5 w-5" /></div><p className="mt-4 text-2xl font-bold text-gray-900">{amount}</p><p className="mt-1 text-sm font-semibold text-gray-800">{title}</p><p className="mt-2 text-xs leading-5 text-gray-500">{description}</p></div>;
}
